import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { retry } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import type { ITokenResponse } from '../user/user.types';
import { store } from 'store';
import { logoutByForce } from 'redux/user/user.thunk';
import {
  getRefreshToken,
  getToken,
  removeRefreshToken,
  removeToken,
  storeRefreshToken,
  storeToken,
} from '../user/user.helper';

const mutex = new Mutex();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URI,
  isJsonContentType: (headers) => {
    const contentType = headers.get('content-type');
    return Boolean(
      contentType?.includes('application/json') ||
      contentType?.includes('application/ld+json') ||
      contentType?.includes('application/merge-patch+json')
    );
  },
  prepareHeaders: (headers: Headers, { arg }) => {
    // token in localStorage :(
    const token = getToken();
    const method = typeof arg === 'string' ? 'GET' : arg?.method || 'GET';

    headers.set('accept', 'application/ld+json');
    headers.set('content-type', 'application/ld+json');
    // PATCH requests need a specific content type
    if (method === 'PATCH') {
      headers.set('Content-Type', 'application/merge-patch+json');
    }
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const customBaseQueryFn: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // if method is PATCH, we need to stringify the body
  // for PUT methods, RTK Query already serializes the body
  if (typeof args === 'object' && args?.method === 'PATCH') {
    if (
      args.body &&
      typeof args.body === 'object' &&
      !(args.body instanceof FormData)
    ) {
      args.body = JSON.stringify(args.body);
    }
  }

  return rawBaseQuery(args, api, extraOptions);
};

export const noAuthCustomBaseQueryFn = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URI,
  isJsonContentType: (headers) => {
    const contentType = headers.get('content-type');
    return Boolean(
      contentType?.includes('application/json') ||
      contentType?.includes('application/ld+json') ||
      contentType?.includes('application/merge-patch+json')
    );
  },
  prepareHeaders: (headers: Headers) => {
    headers.set('accept', 'application/ld+json');
    headers.set('content-type', 'application/ld+json');
    return headers;
  },
});

const resetUser = async () => {
  removeRefreshToken();
  removeToken();
  return store.dispatch(logoutByForce());
};

/**
 * interceptor that tries to reauthenticate, if token is no longer valid
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // if mutex is locked, wait for it to unlock before making the new request
  await mutex.waitForUnlock();
  let result = await customBaseQueryFn(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // check if mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      // check if refresh token is set
      try {
        const refreshToken = getRefreshToken();
        // if refresh token is set, fetch new token with refresh token
        if (refreshToken) {
          const refreshResult = await customBaseQueryFn(
            {
              url: 'api/token/refresh',
              body: {
                refresh_token: refreshToken,
              },
              method: 'POST',
            },
            api,
            extraOptions
          );
          if (refreshResult.data) {
            const {
              token: tokenReceived,
              refresh_token: refreshTokenReceived,
            } = refreshResult.data as ITokenResponse;
            storeToken(tokenReceived);
            storeRefreshToken(refreshTokenReceived);
          } else {
            // handle refreshTokenFetchError: Log out user ?? and remove tokens ?? redirect to login page
            await resetUser();
            // stop retrying
            retry.fail(result.error);
          }
        } else {
          await resetUser();
          // stop retrying
          retry.fail(result.error);
        }
      } finally {
        // we need to release the mutex, so other requests can be made
        release();
      }
    }
    // if mutex is locked, wait for it to unlock and try again
    await mutex.waitForUnlock();
    result = await customBaseQueryFn(args, api, extraOptions);
  }
  return result;
};
